import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Modal,
    Spinner,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "../../../../ui/index.ts";
import { showError } from "../../../../../utils/toast.ts";
import { useEmployeeProfile } from "../../../../../hooks/use-employee-profile.ts";
import { useOffboardingDetail } from "../../hooks/use-offboarding-detail.ts";
import { EmployeeHeaderCard } from "../../components/employee-header-card/index.ts";
import { AllocatedAssetsTable } from "../../components/allocated-assets-table/allocated-assets-table.tsx";
import { OffboardingSectionForm } from "../../components/offboarding-section-form/index.ts";
import { mapPersonalDetailToOffboardingDisplay } from "../../utils/map-employee-profile-display.ts";
import type { OffboardingSectionType } from "../../schemas/index.ts";
import type { OffboardingSource } from "../../types.ts";
import { displayFormatDate } from "../../../../../../utils/helpers.js";
import styles from "./clearance-action-modal.module.css";

function formatLastWorkingDay(finalLwd: string | null | undefined): string {
  if (finalLwd == null || finalLwd.trim() === "") {
    return "TBD";
  }

  const formatted = displayFormatDate(finalLwd);
  return formatted === "-" ? "TBD" : formatted;
}

export interface ClearanceActionModalProps {
  open: boolean;
  source?: OffboardingSource;
  onClose: () => void;
  offboardingId: number;
  sectionType: OffboardingSectionType;
  sectionTypes?: OffboardingSectionType[];
  sectionItems?: ClearanceActionSectionItem[];
  onSuccess: () => void;
}

export type ClearanceActionSectionItem = {
  sectionType: OffboardingSectionType;
  completed: boolean;
};

const SECTION_LABELS: Record<OffboardingSectionType, string> = {
  RETENTION: "Retention",
  RESIGNATION: "Resignation",
  EXIT_INTERVIEW: "Exit Interview",
  ADMIN_CLEARANCE: "Admin Clearance Form",
  IT_CLEARANCE: "IT Clearance Form",
  PAYROLL_CLEARANCE: "Payroll Clearance Form",
  HR_CLEARANCE: "HR Clearance Form",
  RM_CLEARANCE: "Reporting Manager Clearance Form",
};

export function ClearanceActionModal({
  open,
  source = "REGULAR",
  onClose,
  offboardingId,
  sectionType,
  sectionTypes,
  sectionItems,
  onSuccess,
}: ClearanceActionModalProps) {
  const [completedSections, setCompletedSections] = useState<
    Partial<Record<OffboardingSectionType, boolean>>
  >({});
  const [itAllocatedAssetCount, setItAllocatedAssetCount] = useState<
    number | null
  >(null);
  const baseActionSectionItems = useMemo<ClearanceActionSectionItem[]>(
    () =>
      sectionItems?.length
        ? sectionItems
        : (sectionTypes?.length ? sectionTypes : [sectionType]).map((type) => ({
            sectionType: type,
            completed: false,
          })),
    [sectionItems, sectionType, sectionTypes],
  );
  const actionSectionItems = useMemo<ClearanceActionSectionItem[]>(
    () =>
      baseActionSectionItems.map((item) => ({
        ...item,
        completed:
          item.completed || Boolean(completedSections[item.sectionType]),
      })),
    [baseActionSectionItems, completedSections],
  );
  const actionSectionTypes = useMemo(
    () => actionSectionItems.map((item) => item.sectionType),
    [actionSectionItems],
  );
  const firstActionSectionType = actionSectionTypes[0] ?? sectionType;
  const firstPendingSectionType =
    actionSectionItems.find((item) => !item.completed)?.sectionType ??
    firstActionSectionType;
  const actionSectionTypesKey = actionSectionTypes.join("|");
  const readinessKey = `${open ? offboardingId : "closed"}:${actionSectionTypesKey}`;
  const [activeSectionType, setActiveSectionType] =
    useState<OffboardingSectionType>(firstPendingSectionType);
  const [readyState, setReadyState] = useState<{
    key: string;
    sections: Partial<Record<OffboardingSectionType, boolean>>;
  }>({ key: readinessKey, sections: {} });

  const {
    detail,
    loading: detailLoading,
    error: detailError,
  } = useOffboardingDetail(open ? offboardingId : null, source);

  const employeeId = detail?.offboarding.employeeId ?? null;

  const {
    personalDetail,
    loading: profileLoading,
    error: profileError,
  } = useEmployeeProfile(open ? employeeId : null);

  useEffect(() => {
    if (detailError) showError(detailError);
  }, [detailError]);

  useEffect(() => {
    if (profileError) showError(profileError);
  }, [profileError]);

  useEffect(() => {
    setActiveSectionType(firstPendingSectionType);
  }, [firstPendingSectionType, open]);

  useEffect(() => {
    setCompletedSections({});
    setItAllocatedAssetCount(null);
  }, [readinessKey]);

  const handleSectionReadyChange = useCallback(
    (type: OffboardingSectionType, ready: boolean) => {
      setReadyState((prev) => ({
        key: readinessKey,
        sections: {
          ...(prev.key === readinessKey ? prev.sections : {}),
          [type]: ready,
        },
      }));
    },
    [readinessKey],
  );

  const employee =
    personalDetail != null
      ? mapPersonalDetailToOffboardingDisplay(personalDetail)
      : null;

  const lastWorkingDay = formatLastWorkingDay(detail?.offboarding.finalLwd);
  const loading = detailLoading || profileLoading;
  const itAssetSubmitBlockMessage =
    itAllocatedAssetCount == null
      ? "Please wait while allocated assets are checked."
      : itAllocatedAssetCount > 0
        ? "Please deallocate all assigned IT assets before proceeding further."
        : null;
  const formsReady =
    readyState.key === readinessKey &&
    actionSectionItems.every((item) => readyState.sections[item.sectionType]);
  const modalTitle =
    actionSectionItems.length === 1
      ? SECTION_LABELS[firstActionSectionType]
      : "Clearance Forms";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={modalTitle}
      size="xl"
      bodyClassName={styles.body}
    >
      {loading || employee == null ? (
        <Spinner />
      ) : (
        <div className={styles.contentShell}>
          {!formsReady && <Spinner />}
          <div className={formsReady ? styles.content : styles.contentPending}>
            <EmployeeHeaderCard
              employee={employee}
              lastWorkingDay={lastWorkingDay}
              showViewResignationForm={false}
            />
            {actionSectionItems.length > 1 ? (
              <Tabs
                value={activeSectionType}
                onValueChange={(value) =>
                  setActiveSectionType(value as OffboardingSectionType)
                }
              >
                <TabsList className={styles.tabsList}>
                  {actionSectionItems.map(
                    ({ sectionType: type, completed }) => (
                      <TabsTrigger
                        key={type}
                        value={type}
                        className={styles.tabTrigger}
                      >
                        {SECTION_LABELS[type]}
                        {completed ? " (Completed)" : ""}
                      </TabsTrigger>
                    ),
                  )}
                </TabsList>

                {actionSectionItems.map(
                  ({ sectionType: type, completed }, index) => (
                    <TabsContent
                      key={type}
                      value={type}
                      className={styles.tabPanel}
                    >
                      <OffboardingSectionForm
                        offboardingId={offboardingId}
                        source={source}
                        employeeId={employeeId}
                        sectionType={type}
                        submitLabel="Submit"
                        successMessage={`${SECTION_LABELS[type]} submitted successfully.`}
                        showLoadingState={false}
                        readOnly={completed}
                        submitDisabledReason={
                          type === "IT_CLEARANCE"
                            ? itAssetSubmitBlockMessage
                            : null
                        }
                        onReadyChange={(ready) =>
                          handleSectionReadyChange(type, ready)
                        }
                        onSubmitSuccess={() => {
                          setCompletedSections((prev) => ({
                            ...prev,
                            [type]: true,
                          }));
                          onSuccess();
                          const nextSectionType = actionSectionItems
                            .slice(index + 1)
                            .find((item) => !item.completed)?.sectionType;
                          if (nextSectionType != null) {
                            setActiveSectionType(nextSectionType);
                          } else {
                            onClose();
                          }
                        }}
                      />
                      {type === "IT_CLEARANCE" && employeeId != null ? (
                        <AllocatedAssetsTable
                          employeeId={employeeId}
                          employeeName={employee.name}
                          onAssetsCountChange={setItAllocatedAssetCount}
                        />
                      ) : null}
                    </TabsContent>
                  ),
                )}
              </Tabs>
            ) : (
              <>
                {firstActionSectionType === "IT_CLEARANCE" &&
                employeeId != null ? (
                  <AllocatedAssetsTable
                    employeeId={employeeId}
                    employeeName={employee.name}
                    onAssetsCountChange={setItAllocatedAssetCount}
                  />
                ) : null}
                <OffboardingSectionForm
                  offboardingId={offboardingId}
                  source={source}
                  employeeId={employeeId}
                  sectionType={firstActionSectionType}
                  submitLabel="Submit"
                  successMessage={`${SECTION_LABELS[firstActionSectionType]} submitted successfully.`}
                  showLoadingState={false}
                  readOnly={actionSectionItems[0]?.completed ?? false}
                  submitDisabledReason={
                    firstActionSectionType === "IT_CLEARANCE"
                      ? itAssetSubmitBlockMessage
                      : null
                  }
                  onReadyChange={(ready) =>
                    handleSectionReadyChange(firstActionSectionType, ready)
                  }
                  onSubmitSuccess={() => {
                    setCompletedSections((prev) => ({
                      ...prev,
                      [firstActionSectionType]: true,
                    }));
                    onSuccess();
                    onClose();
                  }}
                />
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
